/**
 * TodaysSchedule — Section 3 of the Office Manager Report.
 *
 * Hour-by-hour list of today's sessions in tenant timezone.
 * data-testid: candeza-reports-operations-schedule-*
 */

import React from 'react';
import styled from 'styled-components';
import type { ScheduleSession } from '@/api/reports';
import { formatTime } from '@/api/reports';

// ── Styled components ─────────────────────────────────────────────────────────
//
// Prompt 11E.8: this table had no horizontal-scroll wrapper and no mobile
// fallback — at narrow widths (360px) its 6 columns compressed and text
// overlapped. TableScroll adopts the same overflow-x:auto idea as the
// shared responsive <Table> (src/components/common/Table.tsx) without a
// full migration onto that component.

const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};

  th {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
    color: ${({ theme }) => theme.colors.textSecondary};
    font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
    border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  }

  td {
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  border-radius: 4px;
  padding: 2px 6px;
  background: ${({ $status }) =>
    $status === 'cancelled'
      ? '#fee2e2'
      : $status === 'completed'
      ? '#f3f4f6'
      : '#dbeafe'};
  color: ${({ $status }) =>
    $status === 'cancelled'
      ? '#b91c1c'
      : $status === 'completed'
      ? '#374151'
      : '#1d4ed8'};
`;

const Empty = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

// ── Component ─────────────────────────────────────────────────────────────────

interface TodaysScheduleProps {
  sessions: ScheduleSession[];
  loading?: boolean;
}

export const TodaysSchedule: React.FC<TodaysScheduleProps> = ({ sessions, loading }) => {
  if (loading) {
    return <div data-testid="candeza-reports-operations-schedule-loading">Loading schedule…</div>;
  }

  if (!sessions.length) {
    return (
      <Empty data-testid="candeza-reports-operations-schedule-empty">
        No sessions scheduled today.
      </Empty>
    );
  }

  return (
    <TableScroll data-testid="candeza-reports-operations-schedule-scroll">
      <Table data-testid="candeza-reports-operations-schedule-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Subject</th>
            <th>Teacher</th>
            <th>Room</th>
            <th>Students</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.session_id} data-testid={`candeza-reports-operations-schedule-row-${s.session_id}`}>
              <td>{formatTime(s.starts_at)}</td>
              <td>{s.subject_name}</td>
              <td>{s.teacher_name ?? '—'}</td>
              <td>{s.room_name ?? '—'}</td>
              <td>{s.student_count}</td>
              <td>
                <StatusBadge $status={s.status}>{s.status}</StatusBadge>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
};

